import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../hooks/store';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Slot, Request } from '../../types';
import RequestView from '../../modal/RequestView';
import RequestPost from '../../modal/RequestPost';

export interface AttendeeSlotProps {
	slot: Slot; // Слот который отрисовываем
    request?: Request; // Наш запрос участнику, если есть
    requests: Request[]; // Запросы участника
};

type RequestStatus = 'approved'|'negotiation'|'disabled'|'free';
//
// Отрисовка одного слота в списке слотов.
// В зависимости от того занят слот, назначена встреча у пользователя с этим участником
// или идут переговоры о встрече слот окрашивается в разные цвета.
//
const AttendeeSlot: FC<AttendeeSlotProps> = ({ slot, request, requests }) => {
	const { t, i18n } = useTranslation();
    //const lang = i18n.language === 'en' ? 'en' : 'ru';
	const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const company = i18n.language === 'en' ? 'company_en' : 'company_ru';
    const duration = i18n.language === 'en' ? 'min' : 'мин';
    const attendee = useAppSelector(store => store.attendee);
    const user = useAppSelector(store => store.auth.user);
    const myReq = useAppSelector(store => store.requests);
    const [ isRequestPostOpen, setIsRequestPostOpen ] = useState(false);
    const [ isRequestViewOpen, setIsRequestViewOpen ] = useState(false);
    const [ time, setTime ] = useState('');
    const [ text, setText ] = useState('');
    const [ small, setSmall ] = useState<string|null>('');
    const [ status, setStatus ] = useState<RequestStatus>('free');
    const [ canClick, setCanClick ] = useState(false);

    useEffect(() => {
        let text = t('request.slot.free');
        let small: string|null = '';
        let status: RequestStatus = 'free';
        const [ hour, min ] = slot.start.split('T')[1].split(':');
        setTime(`${hour}:${min}`);
        // Если у нас есть наш запрос на этот слот, но он отменен, то считаем что запроса нет
        const myRequest = request?.status === 'approved' || request?.status === 'negotiation' ? request : null;
        // В requests содеражатся только актуальные запросы пользователя.
        // На них нельзя отправлять новые запросы.
        if(requests.find(r => r.slot_id === slot.id)) {
            status = 'disabled';
            text = t('request.slot.disabled');
        }
        // Если у нас есть свой активный запрос на слот, мы тоже не можем на него кликать.
        if(myReq.find(r => r.slot_id === slot.id && (r.status === 'approved' || r.status === 'negotiation'))) {
            status = 'disabled';
            text = t('request.slot.busy');
        }
        // Если у меня есть запрос на встречу с этим пользователeм, отметим это и запомним компанию.
        if(myRequest?.slot_id === slot.id) {
            if(myRequest.status === 'approved') status = 'approved';
            if(myRequest.status === 'negotiation') status = 'negotiation';
            text = user ? user[name] : '';
            small = user ? user[company] : '';
        }
        // Можем ли кликать по слоту
        if(status === 'approved' || status === 'negotiation' || (status === 'free' && !myRequest)) setCanClick(true);
        else setCanClick(false);
        // Запишем статус и текст
        setText(text);
        setSmall(small);
        setStatus(status);
    }, [ slot, request, requests, t, myReq, name, user, company ]);

    const onClick = () => {
        if(status === 'approved' || status === 'negotiation') {
            setIsRequestViewOpen(true);
        }
        if(status === 'free' && (!request || request.status === 'cancelled' || request.status === 'rejected')) {
            setIsRequestPostOpen(true);
        }
    };

	return (
        <div
            onClick={() => onClick()}
            className={canClick ? `SlotItem ${status}` : `SlotItem ${status} nopointer`}
        >
            <div className='Time'>{time}</div>
            <div className='Duration'>{slot.duration}&nbsp;{duration}</div>
            <div>
                {text}
                {small && <><br/><small>{small}</small></>}
            </div>
            {status === 'approved' && <span className='Tooltip Icon'>
                <Icon icon={faCheck}/>
                <span className='TooltipText'>{t('search.approved')}</span>
            </span>}

            {status === 'negotiation' && <span className='Tooltip Icon'>
                {request?.from_id === attendee?.id ?
                    <Icon icon={faArrowDown}/> :
                    <Icon icon={faArrowUp}/> }
                <span className='TooltipText'>{t('search.negotiation')}</span>
            </span>}

            <RequestPost
                slot={slot}
                to={attendee ?? undefined}
                request={request}
                isOpen={isRequestPostOpen}
                onClose={() => setIsRequestPostOpen(false)}
            />

            <RequestView
                request={request}
                isOpen={isRequestViewOpen}
                onClose={() => setIsRequestViewOpen(false)}
            />
        </div>
	);
};

export default AttendeeSlot;
